import { createContext, useContext, useState, useEffect } from 'react';

const translations = {
  en: {
    // General
    appName: 'ACS Data Viewer 1.0',
    appSubtitle: 'Data Manager',
    adminPanel: 'Admin Panel',
    dashboard: 'Dashboard',
    myData: 'My Data',
    users: 'Users',
    logout: 'Logout',
    login: 'Login',
    cancel: 'Cancel',
    save: 'Save',
    delete: 'Delete',
    confirm: 'Confirm',
    loading: 'Loading...',
    noData: 'No data',
    
    // Login
    loginTitle: 'Login',
    loginDescription: 'Enter your credentials to access',
    username: 'Username',
    password: 'Password',
    yourUsername: 'Your username',
    yourPassword: 'Your password',
    loggingIn: 'Logging in...',
    contactAdmin: 'Contact administrator for access',
    enterEmail: 'Enter your email',
    enterUsername: 'Enter your username',
    enterPassword: 'Enter your password',
    
    // Dashboard
    myDashboard: 'My Dashboard',
    mySalesData: 'My Sales Data',
    totalRecords: 'Total Records',
    selectedRecords: 'Selected',
    lastImport: 'Last Import',
    records: 'records',
    transactionsByMonth: 'Transactions by Month',
    clientOSDistribution: 'Client OS Distribution',
    clientVersions: 'Client Versions',
    
    // Filters
    filters: 'Filters',
    year: 'Year',
    month: 'Month',
    dateRange: 'Date Range',
    from: 'From',
    to: 'To',
    clear: 'Clear',
    thisMonth: 'This Month',
    thisYear: 'This Year',
    allTime: 'All Time',
    customRange: 'Custom Range',
    
    // Admin
    adminDashboard: 'Admin Dashboard',
    manageUsers: 'Manage users and upload Excel files',
    manageUsersAndData: 'Manage users and upload data',
    createUser: 'Create User',
    totalUsers: 'Total Users',
    uploadExcel: 'Upload Excel',
    navigation: 'Navigation',
    settings: 'Settings',
    enterUsername: 'Enter username',
    enterPassword: 'Enter password',
    fillAllFields: 'Please fill all required fields',
    invalidCredentials: 'Invalid credentials',
    changePasswordFor: 'For user',
    viewData: 'View Data',
    deleteRecords: 'Delete Records',
    changePassword: 'Change Password',
    
    // User form
    createNewUser: 'Create New User',
    enterUserData: 'Enter new user data',
    fullName: 'Full Name',
    contactEmail: 'Contact Email',
    creating: 'Creating...',
    
    // Password
    changePasswordTitle: 'Change Password',
    currentPassword: 'Current Password',
    newPassword: 'New Password',
    confirmPassword: 'Confirm Password',
    changing: 'Changing...',
    
    // Upload
    uploadExcelFile: 'Upload Excel File',
    uploadExcelFor: 'Upload Excel file for user',
    dragFile: 'Drag your Excel file here',
    orClick: 'or click to select',
    selectFile: 'Select File',
    acceptedFormats: 'Accepted formats: .xlsx, .xls',
    processing: 'Processing file...',
    
    // Table
    dataTable: 'Data',
    page: 'Page',
    of: 'of',
    previous: 'Previous',
    next: 'Next',
    noDataAvailable: 'No data available',
    contactAdminForData: 'No sales data loaded. Contact administrator.',
    noDataForFilters: 'No records for selected filters',
    showingAllRecords: 'Showing all records',
    clearSort: 'Clear sort',
    records: 'records',
    
    // Delete confirmation
    deleteConfirmTitle: 'Delete Records',
    deleteConfirmMessage: 'Are you sure you want to delete all records for this user? This action cannot be undone.',
    deleting: 'Deleting...',
    
    // Profile
    myProfile: 'My Profile',
    updateContactInfo: 'Update your contact information',
    editProfile: 'Edit profile',
    saving: 'Saving...',
    
    // Messages
    welcome: 'Welcome',
    sessionClosed: 'Session closed',
    userCreated: 'User created successfully',
    passwordUpdated: 'Password updated successfully',
    profileUpdated: 'Profile updated',
    recordsUploaded: 'records uploaded successfully',
    recordsDeleted: 'Records deleted',
    errorLoading: 'Error loading data',
    errorCreating: 'Error creating user',
    errorUpdating: 'Error updating',
    
    // Tooltips
    profileTooltip: 'Click to edit your profile',
    logoutTooltip: 'Sign out of the application',
    dashboardTooltip: 'View your data and statistics',
    adminPanelTooltip: 'Manage users and data',
    createUserTooltip: 'Add a new user to the system',
    changePasswordTooltip: 'Update your security credentials',
    viewDataTooltip: 'View user data and statistics',
    uploadExcelTooltip: 'Upload Excel file for this user',
    deleteRecordsTooltip: 'Delete all records for this user',
    clickToViewData: 'Click to view data',
    
    // Import history
    importHistory: 'Import History',
    noImports: 'No imports yet',
    importedBy: 'Imported by',
    
    // Months
    january: 'January',
    february: 'February',
    march: 'March',
    april: 'April',
    may: 'May',
    june: 'June',
    july: 'July',
    august: 'August',
    september: 'September',
    october: 'October',
    november: 'November',
    december: 'December',
    
    // Password Recovery
    forgotPassword: 'Forgot password?',
    resetPassword: 'Reset Password',
    resetPasswordDescription: 'Enter your email and new password',
    resetPasswordSuccess: 'Password updated successfully',
    userNotFound: 'User not found',
    resetting: 'Resetting...',
    passwordColumn: 'Password',
    resetStep1Desc: 'Enter your registered email address',
    resetStep2Desc: 'Enter the verification code sent to your email',
    resetStep3Desc: 'Create your new password',
    sendCode: 'Send Code',
    codeSent: 'Verification code sent to your email',
    codeSentTo: 'Code sent to',
    verificationCode: 'Verification Code',
    verify: 'Verify',
    codeVerified: 'Code verified successfully',
    invalidCode: 'Invalid verification code',
    back: 'Back',
    passwordsDontMatch: 'Passwords do not match',
    passwordMinLength: 'Password must be at least 4 characters',
    user: 'User',
    
    // User Profile Card
    operatorUrl: 'Operator URL',
    clientName: 'Client Name',
    userProfile: 'User Profile',
    editProfile: 'Edit Profile',
    collapseProfile: 'Collapse',
    expandProfile: 'View Profile',
    profileUpdated: 'Profile updated successfully',
    userType: 'Type',
    userLanguage: 'Language',
    typeACS: 'ACS',
    typeApplicaDigital: 'ApplicaDigital',
    langEnglish: 'English',
    langSpanish: 'Spanish',
    distId: 'DistID',
    
    // Delete User
    deleteUser: 'Delete User',
    deleteUserTitle: 'Delete User',
    deleteUserConfirm1: 'Are you sure you want to delete user',
    deleteUserConfirm2: 'This will permanently delete the user and ALL their data records. This action cannot be undone.',
    deleteUserFinalConfirm: 'FINAL CONFIRMATION: Type "DELETE" to confirm deletion of user',
    typeDelete: 'Type DELETE to confirm',
    userDeleted: 'User and all records deleted successfully',
    deleteUserTooltip: 'Delete user and all data',
    leaveBlankToKeep: 'Leave blank to keep current password',
    
    // Export
    exportData: 'Export',
    exportCSV: 'Export CSV',
    exportExcel: 'Export Excel',
    exporting: 'Exporting...',
    duplicatesSkipped: 'duplicates skipped',
    hoverToReveal: 'Hover to reveal password',
    viewProfileInfo: 'View your profile information',
    close: 'Close',
  },
  es: {
    // General
    appName: 'ACS Data Viewer 1.0',
    appSubtitle: 'Gestor de Datos',
    adminPanel: 'Panel Admin',
    dashboard: 'Dashboard',
    myData: 'Mis Datos',
    users: 'Usuarios',
    logout: 'Cerrar Sesión',
    login: 'Iniciar Sesión',
    cancel: 'Cancelar',
    save: 'Guardar',
    delete: 'Eliminar',
    confirm: 'Confirmar',
    loading: 'Cargando...',
    noData: 'Sin datos',
    
    // Login
    loginTitle: 'Iniciar Sesión',
    loginDescription: 'Ingresa tus credenciales para acceder',
    username: 'Usuario',
    password: 'Contraseña',
    yourUsername: 'Tu nombre de usuario',
    yourPassword: 'Tu contraseña',
    loggingIn: 'Ingresando...',
    contactAdmin: 'Contacta al administrador para obtener acceso',
    enterEmail: 'Ingresa tu correo electrónico',
    enterUsername: 'Ingresa tu usuario',
    enterPassword: 'Ingresa tu contraseña',
    
    // Dashboard
    myDashboard: 'Mi Dashboard',
    mySalesData: 'Mis Datos de Ventas',
    totalRecords: 'Total Registros',
    selectedRecords: 'Seleccionados',
    lastImport: 'Última Importación',
    records: 'registros',
    transactionsByMonth: 'Transacciones por Mes',
    clientOSDistribution: 'Distribución por Sistema Operativo',
    clientVersions: 'Versiones de Cliente',
    
    // Filters
    filters: 'Filtros',
    year: 'Año',
    month: 'Mes',
    dateRange: 'Rango de Fechas',
    from: 'Desde',
    to: 'Hasta',
    clear: 'Limpiar',
    thisMonth: 'Este Mes',
    thisYear: 'Este Año',
    allTime: 'Todo',
    customRange: 'Rango Personalizado',
    
    // Admin
    adminDashboard: 'Panel de Administración',
    manageUsers: 'Gestiona usuarios y carga archivos Excel',
    manageUsersAndData: 'Gestiona usuarios y sube datos',
    createUser: 'Crear Usuario',
    totalUsers: 'Usuarios Totales',
    uploadExcel: 'Subir Excel',
    viewData: 'Ver Datos',
    deleteRecords: 'Eliminar Registros',
    changePassword: 'Cambiar Contraseña',
    navigation: 'Navegación',
    settings: 'Configuración',
    enterUsername: 'Ingresa usuario',
    enterPassword: 'Ingresa contraseña',
    fillAllFields: 'Por favor completa todos los campos',
    invalidCredentials: 'Credenciales inválidas',
    changePasswordFor: 'Para usuario',
    
    // User form
    createNewUser: 'Crear Nuevo Usuario',
    enterUserData: 'Ingresa los datos del nuevo usuario',
    fullName: 'Nombre Completo',
    contactEmail: 'Email de Contacto',
    creating: 'Creando...',
    
    // Password
    changePasswordTitle: 'Cambiar Contraseña',
    currentPassword: 'Contraseña Actual',
    newPassword: 'Nueva Contraseña',
    confirmPassword: 'Confirmar Contraseña',
    changing: 'Cambiando...',
    
    // Upload
    uploadExcelFile: 'Subir Archivo Excel',
    uploadExcelFor: 'Sube un archivo Excel para el usuario',
    dragFile: 'Arrastra tu archivo Excel aquí',
    orClick: 'o haz clic para seleccionar',
    selectFile: 'Seleccionar Archivo',
    acceptedFormats: 'Formatos aceptados: .xlsx, .xls',
    processing: 'Procesando archivo...',
    
    // Table
    dataTable: 'Datos',
    page: 'Página',
    of: 'de',
    previous: 'Anterior',
    next: 'Siguiente',
    noDataAvailable: 'Sin datos disponibles',
    contactAdminForData: 'No tienes datos cargados. Contacta al administrador.',
    noDataForFilters: 'No hay registros para los filtros seleccionados',
    showingAllRecords: 'Mostrando todos los registros',
    clearSort: 'Quitar orden',
    records: 'registros',
    
    // Delete confirmation
    deleteConfirmTitle: 'Eliminar Registros',
    deleteConfirmMessage: '¿Estás seguro de eliminar todos los registros de este usuario? Esta acción no se puede deshacer.',
    deleting: 'Eliminando...',
    
    // Profile
    myProfile: 'Mi Perfil',
    updateContactInfo: 'Actualiza tu información de contacto',
    editProfile: 'Editar perfil',
    saving: 'Guardando...',
    
    // Messages
    welcome: 'Bienvenido',
    sessionClosed: 'Sesión cerrada',
    userCreated: 'Usuario creado exitosamente',
    passwordUpdated: 'Contraseña actualizada exitosamente',
    profileUpdated: 'Perfil actualizado',
    recordsUploaded: 'registros cargados exitosamente',
    recordsDeleted: 'Registros eliminados',
    errorLoading: 'Error al cargar datos',
    errorCreating: 'Error al crear usuario',
    errorUpdating: 'Error al actualizar',
    
    // Tooltips
    profileTooltip: 'Clic para editar tu perfil',
    logoutTooltip: 'Cerrar sesión de la aplicación',
    dashboardTooltip: 'Ver tus datos y estadísticas',
    adminPanelTooltip: 'Gestionar usuarios y datos',
    createUserTooltip: 'Agregar un nuevo usuario al sistema',
    changePasswordTooltip: 'Actualizar tus credenciales de seguridad',
    viewDataTooltip: 'Ver datos y estadísticas del usuario',
    uploadExcelTooltip: 'Subir archivo Excel para este usuario',
    deleteRecordsTooltip: 'Eliminar todos los registros de este usuario',
    clickToViewData: 'Clic para ver datos',
    
    // Import history
    importHistory: 'Historial de Importaciones',
    noImports: 'Sin importaciones aún',
    importedBy: 'Importado por',
    
    // Months
    january: 'Enero',
    february: 'Febrero',
    march: 'Marzo',
    april: 'Abril',
    may: 'Mayo',
    june: 'Junio',
    july: 'Julio',
    august: 'Agosto',
    september: 'Septiembre',
    october: 'Octubre',
    november: 'Noviembre',
    december: 'Diciembre',
    
    // Password Recovery
    forgotPassword: '¿Olvidaste tu contraseña?',
    resetPassword: 'Recuperar Contraseña',
    resetPasswordDescription: 'Ingresa tu correo electrónico y nueva contraseña',
    resetPasswordSuccess: 'Contraseña actualizada exitosamente',
    userNotFound: 'Usuario no encontrado',
    resetting: 'Actualizando...',
    passwordColumn: 'Contraseña',
    resetStep1Desc: 'Ingresa tu correo electrónico registrado',
    resetStep2Desc: 'Ingresa el código de verificación enviado a tu correo',
    resetStep3Desc: 'Crea tu nueva contraseña',
    sendCode: 'Enviar Código',
    codeSent: 'Código de verificación enviado a tu correo',
    codeSentTo: 'Código enviado a',
    verificationCode: 'Código de Verificación',
    verify: 'Verificar',
    codeVerified: 'Código verificado correctamente',
    invalidCode: 'Código de verificación incorrecto',
    back: 'Atrás',
    passwordsDontMatch: 'Las contraseñas no coinciden',
    passwordMinLength: 'La contraseña debe tener al menos 4 caracteres',
    user: 'Usuario',
    
    // User Profile Card
    operatorUrl: 'URL Operador',
    clientName: 'Nombre del Cliente',
    userProfile: 'Ficha de Usuario',
    editProfile: 'Editar Perfil',
    collapseProfile: 'Contraer',
    expandProfile: 'Ver Ficha',
    profileUpdated: 'Perfil actualizado exitosamente',
    userType: 'Tipo',
    userLanguage: 'Idioma',
    typeACS: 'ACS',
    typeApplicaDigital: 'ApplicaDigital',
    langEnglish: 'Inglés',
    langSpanish: 'Español',
    distId: 'DistID',
    
    // Delete User
    deleteUser: 'Eliminar Usuario',
    deleteUserTitle: 'Eliminar Usuario',
    deleteUserConfirm1: '¿Estás seguro de que quieres eliminar al usuario',
    deleteUserConfirm2: 'Esto eliminará permanentemente el usuario y TODOS sus registros de datos. Esta acción no se puede deshacer.',
    deleteUserFinalConfirm: 'CONFIRMACIÓN FINAL: Escribe "ELIMINAR" para confirmar la eliminación del usuario',
    typeDelete: 'Escribe ELIMINAR para confirmar',
    userDeleted: 'Usuario y todos los registros eliminados exitosamente',
    deleteUserTooltip: 'Eliminar usuario y todos sus datos',
    leaveBlankToKeep: 'Dejar en blanco para mantener contraseña actual',
    
    // Export
    exportData: 'Exportar',
    exportCSV: 'Exportar CSV',
    exportExcel: 'Exportar Excel',
    exporting: 'Exportando...',
    duplicatesSkipped: 'duplicados omitidos',
    hoverToReveal: 'Pasa el cursor para ver la contraseña',
    viewProfileInfo: 'Consulta tu información de perfil',
    close: 'Cerrar',
  }
};

const LanguageContext = createContext(null);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('language') || 'en';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const t = (key) => {
    return translations[language][key] || key;
  };

  const getMonths = () => {
    return [
      { value: "1", label: t('january') },
      { value: "2", label: t('february') },
      { value: "3", label: t('march') },
      { value: "4", label: t('april') },
      { value: "5", label: t('may') },
      { value: "6", label: t('june') },
      { value: "7", label: t('july') },
      { value: "8", label: t('august') },
      { value: "9", label: t('september') },
      { value: "10", label: t('october') },
      { value: "11", label: t('november') },
      { value: "12", label: t('december') },
    ];
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, getMonths }}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
